import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { api } from './api';
import { t } from './i18n';
import { CampaignHome, DiscoveriesPage } from './pages/CampaignHome';
import { CampaignLayout } from './pages/CampaignLayout';
import { CampaignsPage } from './pages/CampaignsPage';
import { CombatListPage, CombatPage } from './pages/CombatPage';
import { GraphPage } from './pages/GraphPage';
import { ImportPage } from './pages/ImportPage';
import { LoginPage } from './pages/LoginPage';
import { AccountPage } from './pages/AccountPage';
import { InvitePage } from './pages/InvitePage';
import { NotePage } from './pages/NotePage';
import { OpenLinksPage } from './pages/OpenLinksPage';
import { PlacesPage } from './pages/PlacesPage';
import { PollPage } from './pages/PollPage';
import { PollsPage } from './pages/PollsPage';
import { PublicPollPage } from './pages/PublicPollPage';
import { QuestsPage } from './pages/QuestsPage';
import { SessionPage } from './pages/SessionPage';
import { SearchPage } from './pages/SearchPage';
import { SettingsPage } from './pages/SettingsPage';
import { CatalogPage } from './pages/CatalogPage';
import { MapPage } from './pages/MapPage';
import { ScreenPage } from './pages/ScreenPage';
import { SyncStatus } from './components/SyncStatus';

/** Hinweis, wenn der Server eine neuere Version ausliefert als dieser Tab. */
function OutdatedBanner() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const on = () => setShow(true);
    window.addEventListener('kt:outdated', on);
    return () => window.removeEventListener('kt:outdated', on);
  }, []);
  if (!show) return null;
  return (
    <div className="outdated-banner" role="alert">
      {t('app.outdated')}
      <button className="primary" onClick={() => window.location.reload()}>
        {t('app.reload')}
      </button>
    </div>
  );
}

export function App() {
  const location = useLocation();
  // Spieleransicht: öffentlich über den geheimen Link, ohne Login
  if (location.pathname.startsWith('/screen/')) {
    return (
      <Routes>
        <Route path="/screen/:token" element={<ScreenPage />} />
      </Routes>
    );
  }
  // Terminumfrage: persönlicher Link, ohne Konto
  if (location.pathname.startsWith('/poll/')) {
    return (
      <Routes>
        <Route path="/poll/:token" element={<PublicPollPage />} />
      </Routes>
    );
  }
  return (
    <>
      <OutdatedBanner />
      <SyncStatus />
      <AppRoutes />
    </>
  );
}

function AppRoutes() {
  const location = useLocation();
  const auth = useQuery({ queryKey: ['auth'], queryFn: api.authState, staleTime: Infinity });

  if (auth.isLoading) return <div className="center-screen muted">{t('common.loading')}</div>;
  if (auth.isError) return <div className="center-screen error">{(auth.error as Error).message}</div>;

  const state = auth.data!;
  // Einladungslink: funktioniert angemeldet und ohne Konto
  if (location.pathname.startsWith('/invite/')) {
    return (
      <Routes>
        <Route path="/invite/:token" element={<InvitePage />} />
      </Routes>
    );
  }
  if (!state.user) return <LoginPage setup={state.needsSetup} />;

  return (
    <Routes>
      <Route path="/" element={<CampaignsPage />} />
      <Route path="/account" element={<AccountPage />} />
      <Route path="/import" element={<ImportPage />} />
      <Route path="/c/:cid" element={<CampaignLayout />}>
        <Route index element={<CampaignHome />} />
        <Route path="n/:nid" element={<NotePage />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="discoveries" element={<DiscoveriesPage />} />
        <Route path="graph" element={<GraphPage />} />
        <Route path="places" element={<PlacesPage />} />
        <Route path="session" element={<SessionPage />} />
        <Route path="quests" element={<QuestsPage />} />
        <Route path="polls" element={<PollsPage />} />
        <Route path="polls/:pid" element={<PollPage />} />
        <Route path="combat" element={<CombatListPage />} />
        <Route path="combat/:kid" element={<CombatPage />} />
        <Route path="open-links" element={<OpenLinksPage />} />
        <Route path="import" element={<ImportPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="catalog" element={<CatalogPage />} />
        <Route path="map/:nid" element={<MapPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
